import { ChatHtmlRenderer } from '@/features/chat/ChatHtmlRenderer';

import type { HintResponse, AIHomeworkVoteState } from './aiHomeworkTypes';
import { VoteButtons } from './VoteButtons';
import styles from './AIHintDisplay.module.css';

interface AIHintDisplayProps {
  error: string | null;
  isLoading: boolean;
  isRegenerating: boolean;
  onDownvote: () => void;
  onUpvote: () => void;
  response: HintResponse | null;
  statusMessage: string | null;
  vote: AIHomeworkVoteState;
}

export function AIHintDisplay({
  error,
  isLoading,
  isRegenerating,
  onDownvote,
  onUpvote,
  response,
  statusMessage,
  vote,
}: AIHintDisplayProps) {
  if (!isLoading && !isRegenerating && !error && !statusMessage && !response) {
    return null;
  }

  return (
    <section className={styles.panel} aria-label="AI hint">
      <div className={styles.header}>
        <h4 className={styles.title}>AI Hint</h4>
        <p className={styles.caption}>
          A guided nudge for this question, without giving away the solution.
        </p>
      </div>

      {isLoading ? (
        <p className={styles.status} role="status">
          Generating a hint...
        </p>
      ) : null}

      {isRegenerating ? (
        <div className={styles.regenerating} role="status" aria-live="polite">
          <span className={styles.spinner} aria-hidden="true" />
          <p>Sorry about that, let me try again…</p>
        </div>
      ) : null}

      {statusMessage ? (
        <p className={styles.status} role="status">
          {statusMessage}
        </p>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      {response && !isRegenerating ? (
        <>
          <div className={styles.content}>
            {/* Sanitized rich HTML, not markdown: MarkdownRenderer is mounted
                with skipHtml and drops images and raw markup entirely. */}
            {response.blocks.map((block, index) => (
              <ChatHtmlRenderer fullWidth html={block.html} key={index} />
            ))}
          </div>
          <VoteButtons
            downvotes={response.downvotes}
            onDownvote={onDownvote}
            onUpvote={onUpvote}
            upvotes={response.upvotes}
            vote={vote}
          />
        </>
      ) : null}
    </section>
  );
}
